/**
 * Market Map Management Page
 * Admin page to manage market maps
 */
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  HiOutlineMap,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineEye,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineHome,
  HiOutlineOfficeBuilding,
  HiOutlineRefresh,
  HiOutlineExclamation,
  HiOutlineLockClosed,
  HiOutlineDuplicate,
} from 'react-icons/hi'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import marketMapService from '../../services/marketMapService'

function MarketMapManagement() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [maps, setMaps] = useState([])
  const [activeTab, setActiveTab] = useState('all')
  const [settings, setSettings] = useState({})

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: null, // 'activate' | 'deactivate'
    map: null,
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [mapsRes, settingsRes] = await Promise.all([
        marketMapService.getAllMaps({ per_page: 100 }),
        marketMapService.getSettings(),
      ])

      if (mapsRes.status === 'success') {
        setMaps(mapsRes.data || [])
      }

      if (settingsRes.status === 'success') {
        setSettings(settingsRes.data || {})
      }
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Không thể tải dữ liệu')
    } finally {
      setLoading(false)
    }
  }

  // Open confirm dialog for activate
  const openActivateConfirm = (map) => {
    setConfirmDialog({
      isOpen: true,
      type: 'activate',
      map,
    })
  }

  // Open confirm dialog for deactivate
  const openDeactivateConfirm = (map) => {
    setConfirmDialog({
      isOpen: true,
      type: 'deactivate',
      map,
    })
  }

  // Close confirm dialog
  const closeConfirmDialog = () => {
    setConfirmDialog({ isOpen: false, type: null, map: null })
  }

  // Confirm activate action
  const handleConfirmActivate = async () => {
    if (!confirmDialog.map) return

    try {
      const response = await marketMapService.activateMap(confirmDialog.map.id)
      if (response.status === 'success') {
        toast.success('Đã kích hoạt bản đồ')
        loadData()
      }
    } catch (error) {
      console.error('Failed to activate:', error)
      toast.error('Không thể kích hoạt')
    } finally {
      closeConfirmDialog()
    }
  }

  // Confirm deactivate action
  const handleConfirmDeactivate = async () => {
    if (!confirmDialog.map) return

    try {
      const response = await marketMapService.deactivateMap(confirmDialog.map.id)
      if (response.status === 'success') {
        toast.success('Đã hủy kích hoạt')
        loadData()
      }
    } catch (error) {
      console.error('Failed to deactivate:', error)
      toast.error('Không thể hủy kích hoạt')
    } finally {
      closeConfirmDialog()
    }
  }

  const handleDelete = async (mapId) => {
    if (!confirm('Xóa bản đồ này? Tất cả markers cũng sẽ bị xóa.')) return

    try {
      const response = await marketMapService.deleteMap(mapId)
      if (response.status === 'success') {
        toast.success('Đã xóa bản đồ')
        loadData()
      }
    } catch (error) {
      console.error('Failed to delete:', error)
      toast.error('Không thể xóa')
    }
  }

  const handleClone = async (map) => {
    const name = prompt('Tên cho bản sao:', `${map.name} (Bản sao)`)
    if (name === null) return  // hủy
    const tid = toast.loading('Đang tạo bản sao...')
    try {
      const response = await marketMapService.cloneMap(map.id, name.trim() || undefined)
      if (response.status === 'success') {
        toast.success(response.message || 'Đã tạo bản sao', { id: tid })
        // chuyển thẳng sang editor của bản sao để sửa nhanh
        if (response.data?.id) navigate(`/admin/market-maps/${response.data.id}`)
        else loadData()
      } else {
        toast.error(response.message || 'Không thể tạo bản sao', { id: tid })
      }
    } catch (error) {
      console.error('Failed to clone:', error)
      toast.error(error.response?.data?.message || 'Không thể tạo bản sao', { id: tid })
    }
  }

  const handleUpdateDefaultTab = async (tab) => {
    try {
      await marketMapService.updateSettings({ default_tab: tab })
      toast.success('Đã cập nhật tab mặc định')
      setSettings((prev) => ({ ...prev, default_tab: tab }))
    } catch (error) {
      console.error('Failed to update settings:', error)
      toast.error('Không thể cập nhật')
    }
  }

  const handleUpdateDefaultMap = async (type, mapId) => {
    const key = type === 'cho_thue' ? 'default_map_cho_thue' : 'default_map_mua_ban'
    try {
      await marketMapService.updateSettings({ [key]: mapId || '' })
      toast.success('Đã cập nhật bản đồ mặc định')
      setSettings((prev) => ({ ...prev, [key]: mapId ? parseInt(mapId) : null }))
    } catch (error) {
      console.error('Failed to update settings:', error)
      toast.error('Không thể cập nhật')
    }
  }

  // Get active maps by type for dropdowns
  const activeMapsChoThue = maps.filter((m) => m.map_type === 'cho_thue' && m.is_active)
  const activeMapsMuaBan = maps.filter((m) => m.map_type === 'mua_ban' && m.is_active)

  // Filter maps by type
  const filteredMaps =
    activeTab === 'all' ? maps : maps.filter((m) => m.map_type === activeTab)

  // Group by type for stats
  const choThueCount = maps.filter((m) => m.map_type === 'cho_thue').length
  const muaBanCount = maps.filter((m) => m.map_type === 'mua_ban').length
  const activeChoThue = maps.find(
    (m) => m.map_type === 'cho_thue' && m.is_active
  )
  const activeMuaBan = maps.find((m) => m.map_type === 'mua_ban' && m.is_active)

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <HiOutlineMap className="w-6 h-6 mr-2 text-primary-500" />
            Quản lý Bản đồ Giá
          </h1>
          <p className="text-gray-500 mt-1">
            Tạo và quản lý bản đồ giá thị trường hiển thị cho tất cả users
          </p>
        </div>

        <div className="flex gap-3">
          <button onClick={loadData} className="btn-outline">
            <HiOutlineRefresh className="w-5 h-5 mr-1" />
            Làm mới
          </button>
          <Link to="/admin/market-maps/create" className="btn-primary">
            <HiOutlinePlus className="w-5 h-5 mr-1" />
            Tạo bản đồ mới
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <HiOutlineHome className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Cho thuê</div>
              <div className="text-xl font-bold text-gray-900">
                {choThueCount} bản đồ
              </div>
            </div>
          </div>
          {activeChoThue && (
            <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
              <HiOutlineCheck className="w-4 h-4" />
              Active: {activeChoThue.name}
            </div>
          )}
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <HiOutlineOfficeBuilding className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Mua bán</div>
              <div className="text-xl font-bold text-gray-900">
                {muaBanCount} bản đồ
              </div>
            </div>
          </div>
          {activeMuaBan && (
            <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
              <HiOutlineCheck className="w-4 h-4" />
              Active: {activeMuaBan.name}
            </div>
          )}
        </div>

        <div className="card p-4 sm:col-span-2">
          <div className="text-sm text-gray-500 mb-3">Cài đặt bản đồ mặc định</div>

          {/* Tab mặc định */}
          <div className="mb-4">
            <div className="text-xs text-gray-400 mb-1.5">Tab hiển thị đầu tiên</div>
            <div className="flex gap-2">
              <button
                onClick={() => handleUpdateDefaultTab('cho_thue')}
                className={clsx(
                  'flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors',
                  settings.default_tab === 'cho_thue'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                Cho thuê
              </button>
              <button
                onClick={() => handleUpdateDefaultTab('mua_ban')}
                className={clsx(
                  'flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors',
                  settings.default_tab === 'mua_ban'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                Mua bán
              </button>
            </div>
          </div>

          {/* Bản đồ mặc định cho mỗi loại */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <HiOutlineHome className="w-3 h-3 text-blue-500" />
                BĐ Cho thuê
              </div>
              <select
                value={settings.default_map_cho_thue || ''}
                onChange={(e) => handleUpdateDefaultMap('cho_thue', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Chọn bản đồ --</option>
                {activeMapsChoThue.map((map) => (
                  <option key={map.id} value={map.id}>
                    {map.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <HiOutlineOfficeBuilding className="w-3 h-3 text-purple-500" />
                BĐ Mua bán
              </div>
              <select
                value={settings.default_map_mua_ban || ''}
                onChange={(e) => handleUpdateDefaultMap('mua_ban', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">-- Chọn bản đồ --</option>
                {activeMapsMuaBan.map((map) => (
                  <option key={map.id} value={map.id}>
                    {map.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'all', label: 'Tất cả' },
          { id: 'cho_thue', label: 'Cho thuê' },
          { id: 'mua_ban', label: 'Mua bán' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Maps List */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-2" />
            <p className="text-gray-500">Đang tải...</p>
          </div>
        ) : filteredMaps.length > 0 ? (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden divide-y divide-gray-100">
              {filteredMaps.map((map) => (
                <div key={map.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/map/${map.id}`}
                          className="font-medium text-gray-900 truncate hover:text-primary-600 hover:underline transition-colors"
                          title="Xem bản đồ"
                        >
                          {map.name}
                        </Link>
                        {map.is_private && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 flex items-center gap-1 flex-shrink-0">
                            <HiOutlineLockClosed className="w-3 h-3" />
                            Private
                          </span>
                        )}
                        {map.is_active ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 flex items-center gap-1 flex-shrink-0">
                            <HiOutlineCheck className="w-3 h-3" />
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 flex-shrink-0">
                            Draft
                          </span>
                        )}
                      </div>
                      {map.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                          {map.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span
                          className={clsx(
                            'px-2 py-0.5 rounded-full text-[10px] font-medium',
                            map.map_type === 'cho_thue'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          )}
                        >
                          {map.map_type === 'cho_thue' ? 'Cho thuê' : 'Mua bán'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {map.marker_count || 0} markers
                        </span>
                        {map.creator_name && (
                          <span className="text-xs text-gray-400">
                            • {map.creator_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Actions */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                    <Link
                      to={`/map/${map.id}`}
                      target="_blank"
                      className="p-2.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      title="Xem bản đồ"
                    >
                      <HiOutlineEye className="w-4 h-4 text-blue-500" />
                    </Link>
                    <Link
                      to={`/admin/market-maps/${map.id}`}
                      className="flex-1 btn-sm btn-outline text-center justify-center"
                    >
                      <HiOutlinePencil className="w-4 h-4 mr-1" />
                      Chỉnh sửa
                    </Link>
                    {map.is_active ? (
                      <button
                        onClick={() => openDeactivateConfirm(map)}
                        className="p-2.5 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                        title="Hủy kích hoạt"
                      >
                        <HiOutlineX className="w-4 h-4 text-red-500" />
                      </button>
                    ) : (
                      <button
                        onClick={() => openActivateConfirm(map)}
                        className="p-2.5 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                        title="Kích hoạt"
                      >
                        <HiOutlineCheck className="w-4 h-4 text-green-500" />
                      </button>
                    )}
                    <button
                      onClick={() => handleClone(map)}
                      className="p-2.5 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                      title="Tạo bản sao"
                    >
                      <HiOutlineDuplicate className="w-4 h-4 text-amber-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(map.id)}
                      className="p-2.5 bg-gray-50 hover:bg-red-50 rounded-lg transition-colors"
                      title="Xóa"
                    >
                      <HiOutlineTrash className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Tên bản đồ
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Loại
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Markers
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Trạng thái
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Người tạo
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Ngày tạo
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredMaps.map((map) => (
                    <tr key={map.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <Link
                            to={`/map/${map.id}`}
                            className="font-medium text-gray-900 hover:text-primary-600 hover:underline transition-colors"
                            title="Xem bản đồ"
                          >
                            {map.name}
                          </Link>
                          {map.description && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">
                              {map.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={clsx(
                            'px-2 py-1 rounded-full text-xs font-medium',
                            map.map_type === 'cho_thue'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          )}
                        >
                          {map.map_type === 'cho_thue' ? 'Cho thuê' : 'Mua bán'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {map.marker_count || 0} markers
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {map.is_private && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1 w-fit">
                              <HiOutlineLockClosed className="w-3 h-3" />
                              Private
                            </span>
                          )}
                          {map.is_active ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                              <HiOutlineCheck className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                              Draft
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {map.creator_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {map.created_at
                          ? new Date(map.created_at).toLocaleDateString('vi-VN')
                          : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/map/${map.id}`}
                            target="_blank"
                            className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Xem bản đồ"
                          >
                            <HiOutlineEye className="w-4 h-4 text-blue-500" />
                          </Link>
                          <Link
                            to={`/admin/market-maps/${map.id}`}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Chỉnh sửa"
                          >
                            <HiOutlinePencil className="w-4 h-4 text-gray-500" />
                          </Link>

                          {map.is_active ? (
                            <button
                              onClick={() => openDeactivateConfirm(map)}
                              className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                              title="Hủy kích hoạt"
                            >
                              <HiOutlineX className="w-4 h-4 text-red-500" />
                            </button>
                          ) : (
                            <button
                              onClick={() => openActivateConfirm(map)}
                              className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                              title="Kích hoạt"
                            >
                              <HiOutlineCheck className="w-4 h-4 text-green-500" />
                            </button>
                          )}

                          <button
                            onClick={() => handleClone(map)}
                            className="p-2 hover:bg-amber-100 rounded-lg transition-colors"
                            title="Tạo bản sao"
                          >
                            <HiOutlineDuplicate className="w-4 h-4 text-amber-600" />
                          </button>

                          <button
                            onClick={() => handleDelete(map.id)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                            title="Xóa"
                          >
                            <HiOutlineTrash className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <HiOutlineMap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Chưa có bản đồ nào</p>
            <Link
              to="/admin/market-maps/create"
              className="btn-primary mt-4 inline-flex"
            >
              <HiOutlinePlus className="w-5 h-5 mr-1" />
              Tạo bản đồ đầu tiên
            </Link>
          </div>
        )}
      </div>

      {/* Confirm Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className={clsx(
                'p-2 rounded-full',
                confirmDialog.type === 'activate' ? 'bg-green-100' : 'bg-red-100'
              )}>
                <HiOutlineExclamation className={clsx(
                  'w-6 h-6',
                  confirmDialog.type === 'activate' ? 'text-green-600' : 'text-red-600'
                )} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {confirmDialog.type === 'activate' ? 'Kích hoạt bản đồ' : 'Hủy kích hoạt bản đồ'}
              </h3>
            </div>

            <div className="mb-6">
              {confirmDialog.type === 'activate' ? (
                <p className="text-gray-600">
                  Bạn có chắc muốn kích hoạt bản đồ <strong className="text-gray-900">"{confirmDialog.map?.name}"</strong>?
                  <br />
                  <span className="text-green-600 text-sm mt-2 block">
                    Bản đồ sẽ hiển thị cho người dùng trên trang /map.
                  </span>
                </p>
              ) : (
                <p className="text-gray-600">
                  Bạn có chắc muốn hủy kích hoạt bản đồ <strong className="text-gray-900">"{confirmDialog.map?.name}"</strong>?
                  <br />
                  <span className="text-amber-600 text-sm mt-2 block">
                    Người dùng sẽ không thể xem bản đồ này trên trang /map.
                  </span>
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={closeConfirmDialog}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={confirmDialog.type === 'activate' ? handleConfirmActivate : handleConfirmDeactivate}
                className={clsx(
                  'px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors',
                  confirmDialog.type === 'activate'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                )}
              >
                {confirmDialog.type === 'activate' ? 'Kích hoạt' : 'Hủy kích hoạt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketMapManagement
